import { useEffect, useRef, useState } from 'react'
import { usePageContext, useChatMetadata, useAgentRuntime } from '@multiplayer-app/ai-agent-react'
import { buildDocSnapshot, isSnapshotEmpty, type DocSnapshot } from '@/shared/office/snapshot'
import { DocSyncSession, docSyncLog, docSyncVerbose } from '@/shared/office/docSync'
import { startExcelMirror, type ExcelMirrorHandle } from '@/shared/office/excelSync'
import { detectHost, ensureDocumentId } from '@/shared/office/host'
import { getStoredAuth } from '@/shared/auth/login'

/**
 * The composer builds its page-context attachment when the draft is created and
 * only rebuilds it on route changes (popstate/hashchange/this event). The pane
 * has no routing, so we fire the library's own location-change event after each
 * snapshot refresh to get the attachment rebuilt with current document data.
 */
const AGENT_LOCATION_CHANGE_EVENT = 'mp-agent:locationchange'

/** Doc-sync state surfaced to the pane UI ('disabled' = host not mirrored). */
export type DocSyncIndicatorState = 'disabled' | 'connecting' | 'synced' | 'offline'

/**
 * Keeps a cached snapshot of the active Office selection and attaches it to every
 * outgoing message via `usePageContext`. The page-context provider must be
 * synchronous, so we refresh the cache on the document selection-changed event
 * (and once on mount) and serve the latest cached value.
 *
 * Returns the doc-sync state for the pane's sync indicator.
 */
export function useOfficePageContext(): DocSyncIndicatorState {
  const runtime = useAgentRuntime()
  const snapshotRef = useRef<DocSnapshot | undefined>(undefined)
  const requestRefreshRef = useRef<() => void>()
  const [syncState, setSyncState] = useState<DocSyncIndicatorState>('disabled')

  useEffect(() => {
    let disposed = false
    let refreshSeq = 0
    let debounceTimer: number | undefined
    let retryTimer: number | undefined

    // Word documents and Excel workbooks are mirrored to the AI service for
    // the whole pane session so the agent can read beyond the snapshot cap
    // (see LARGE_DOCUMENT_PLAN.md). The provider remounts this hook on token
    // change, keeping auth current.
    const host = detectHost()
    let docSync: DocSyncSession | undefined
    let excelMirror: ExcelMirrorHandle | undefined
    if (host === 'word' || host === 'excel') {
      const token = getStoredAuth()?.token
      if (token) {
        try {
          docSync = new DocSyncSession(token)
          if (host === 'excel') excelMirror = startExcelMirror(docSync)
        } catch (error) {
          docSyncLog('doc-sync unavailable:', error)
        }
      } else {
        docSyncLog(`host=${host} but no stored token — mirroring disabled`)
      }
    }
    docSyncLog(`host=${host}`, docSync ? `mirroring as session ${docSync.sessionId}` : 'mirroring disabled')

    const refresh = async (attempt = 0) => {
      const seq = ++refreshSeq
      const snapshot = await buildDocSnapshot(docSync)
      // Snapshot reads the full document body, so a burst of refreshes can
      // resolve out of order — only the latest started read may win, otherwise
      // a slow stale read overwrites the current selection.
      if (disposed || seq !== refreshSeq) return
      snapshotRef.current = snapshot

      // Mirror the live selection as awareness so the agent can re-check it
      // mid-turn (page context keeps carrying the send-time selection).
      docSync?.publishSelection(snapshot.host, snapshot.selectionText, snapshot.selectionAddress)
      if (docSyncVerbose) {
        docSyncLog(
          `snapshot: synced=${snapshot.documentSynced === true} session=${snapshot.docSessionId ?? '—'} ` +
            `paragraphs=${snapshot.documentParagraphCount ?? '—'} fallbackText=${snapshot.documentText !== undefined}`,
          snapshot
        )
      }
      window.dispatchEvent(new Event(AGENT_LOCATION_CHANGE_EVENT))
      // Right after the pane loads, Office.js reads can fail before the host
      // finishes binding; retry briefly so the first message carries content.
      if (isSnapshotEmpty(snapshot) && attempt < 3) {
        window.clearTimeout(retryTimer)
        retryTimer = window.setTimeout(() => {
          void refresh(attempt + 1)
        }, 500)
      }
    }

    // Word fires DocumentSelectionChanged repeatedly while a selection is being
    // dragged; collapse the burst into one read.
    const scheduleRefresh = () => {
      window.clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(() => {
        void refresh()
      }, 150)
      // A session that exhausted its reconnect budget gets a fresh round on
      // user activity — recovers after backend restarts without a pane reload.
      docSync?.resumeIfSuspended()
      // Older Excel hosts lack workbook content events; fall back to
      // re-mirroring on the same coarse triggers as the snapshot.
      if (excelMirror && !excelMirror.eventsSupported) excelMirror.requestRefresh()
    }

    const updateSyncState = () => {
      if (!disposed) setSyncState(docSync ? docSync.state : 'disabled')
    }

    requestRefreshRef.current = scheduleRefresh
    void refresh()
    updateSyncState()
    // Rebuild the snapshot when the mirror connects/disconnects so the slim
    // (or fallback) form is attached without waiting for a selection change,
    // and keep the pane's sync indicator current from the same events.
    docSync?.onStatusChange(() => {
      scheduleRefresh()
      updateSyncState()
    })

    const doc = Office?.context?.document
    if (doc?.addHandlerAsync) {
      doc.addHandlerAsync(Office.EventType.DocumentSelectionChanged, scheduleRefresh)
    }
    // The selection event is not fully reliable; also refresh when focus enters
    // the task pane — the user selects in the document, then clicks into the
    // composer, so this runs right before they type/send.
    window.addEventListener('focus', scheduleRefresh, true)

    return () => {
      disposed = true
      excelMirror?.dispose()
      docSync?.destroy()
      window.clearTimeout(debounceTimer)
      window.clearTimeout(retryTimer)
      window.removeEventListener('focus', scheduleRefresh, true)
      if (doc?.removeHandlerAsync) {
        doc.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, { handler: scheduleRefresh })
      }
    }
  }, [])

  // Mint the stable document id lazily — only once the user engages a chat
  // (activeChatId set) — so passively opening a document never marks it
  // modified. Refresh afterwards so the next new chat's metadata carries the id.
  useEffect(() => {
    let done = false
    let unsubscribe = () => {}
    const tryMint = () => {
      if (done || !runtime.store.getState().activeChatId) return
      done = true
      unsubscribe()
      void ensureDocumentId().then((id) => {
        if (id) requestRefreshRef.current?.()
      })
    }
    unsubscribe = runtime.store.subscribe(tryMint)
    tryMint()
    return () => unsubscribe()
  }, [runtime])

  usePageContext(() => {
    const snapshot = snapshotRef.current
    if (!snapshot) return undefined
    // Surface the document identity as top-level page-context metadata (stable
    // across selection changes) in addition to the full selection snapshot.
    return {
      office: snapshot as unknown as Record<string, unknown>,
      documentName: snapshot.documentName,
      documentPath: snapshot.documentPath,
      documentId: snapshot.documentId
    }
  }, [])

  // Document identity + live docSessionId carried in chat metadata. This rides
  // every message (not just chat creation) and — unlike the page-context
  // attachment — the user cannot dismiss it, so the AI service can always
  // resolve which mirrored document this chat is about. docSessionId is the
  // authoritative key the office read tools use; documentName/documentPath are
  // also persisted onto the chat entity at creation for history/queries.
  useChatMetadata(() => {
    const snapshot = snapshotRef.current
    if (!snapshot || (!snapshot.documentName && !snapshot.docSessionId && !snapshot.documentId)) return undefined
    return {
      ...(snapshot.documentName ? { documentName: snapshot.documentName } : {}),
      ...(snapshot.documentPath ? { documentPath: snapshot.documentPath } : {}),
      // Stable grouping key the chat-history filter matches on (survives
      // rename/move, unlike documentName/documentPath).
      ...(snapshot.documentId ? { documentId: snapshot.documentId } : {}),
      ...(snapshot.docSessionId ? { docSessionId: snapshot.docSessionId } : {})
    }
  }, [])

  return syncState
}
